/**
 * CancelBundleModal - Modal for cancelling an NFT bundle
 *
 * Flow:
 * 1. Show confirmation
 * 2. Sign offer cancellation transactions via Xaman (one per NFT)
 * 3. Bundle is marked as cancelled
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { FeatherX, FeatherCheck, FeatherLoader, FeatherAlertTriangle } from '@subframe/core';
import {
  cancelBundle,
  type NFTBundle,
  type XamanPayload,
  type SigningIntentRef,
} from '../../api/bundles';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { formatPrice } from '../../utils/formatters';
import { toSigningPayload, getWalletProvider } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import SigningStepMonitor from '../trades/SigningStepMonitor';

/** Backend cancel payload entry (Xaman QR payload and/or client-signing intent). */
type CancelPayloadEntry = { nftTokenId: string; payload?: XamanPayload; signingIntent?: SigningIntentRef };

interface CancelBundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  bundle: NFTBundle;
  onSuccess?: () => void;
}

type ModalStep = 'confirm' | 'signing' | 'success' | 'error';

interface SigningProgress {
  currentIndex: number;
  totalCount: number;
  currentNftName: string;
  completedCount: number;
}

export const CancelBundleModal: React.FC<CancelBundleModalProps> = ({
  isOpen,
  onClose,
  bundle,
  onSuccess,
}) => {
  const { user } = useAuthStore();
  const { success: showSuccess } = useNotificationStore();

  const [step, setStep] = useState<ModalStep>('confirm');
  const [error, setError] = useState<string>('');
  const [payloads, setPayloads] = useState<CancelPayloadEntry[]>([]);
  const [currentPayload, setCurrentPayload] = useState<CancelPayloadEntry | null>(null);
  const [signingProgress, setSigningProgress] = useState<SigningProgress | null>(null);
  const [websockets, setWebsockets] = useState<WebSocket[]>([]);
  const [signedIndices, setSignedIndices] = useState<Set<number>>(new Set());

  // Client-signing (joey-wc): sequential resolution via SigningStepMonitor (no QR/WS).
  const [isClientSigning, setIsClientSigning] = useState(false);
  const [normalizedPayloads, setNormalizedPayloads] = useState<Array<{ nftTokenId: string; payload: SigningPayload }>>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const wcConfirmedRef = useRef<Set<number>>(new Set());

  // Polling fallback state (per-payload)
  const pollingIntervalsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const hasReachedFinalStateRef = useRef<boolean>(false);
  const MAX_POLLING_ATTEMPTS = 300;
  const POLLING_INTERVAL = 1000;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('confirm');
      setError('');
      setPayloads([]);
      setCurrentPayload(null);
      setSigningProgress(null);
      setSignedIndices(new Set());
      hasReachedFinalStateRef.current = false;
      setIsClientSigning(false);
      setNormalizedPayloads([]);
      setCurrentStepIndex(0);
      wcConfirmedRef.current = new Set();
    }
  }, [isOpen]);

  // Cleanup websockets and polling on unmount
  useEffect(() => {
    return () => {
      websockets.forEach(ws => ws.close());
      pollingIntervalsRef.current.forEach(interval => clearInterval(interval));
      pollingIntervalsRef.current.clear();
    };
  }, [websockets]);

  // Get NFT name from bundle items
  const getNftName = useCallback((nftTokenId: string) => {
    const item = bundle.items?.find(i => i.nftTokenId === nftTokenId);
    return item?.nft_name || nftTokenId.slice(0, 12) + '...';
  }, [bundle.items]);

  // Set up WebSocket listeners for ALL cancel payloads (with per-payload polling fallback)
  const setupAllWebsockets = useCallback((
    allPayloads: CancelPayloadEntry[]
  ) => {
    const confirmedSet = new Set<number>();
    const newWebsockets: WebSocket[] = [];
    const pollingAttemptsMap = new Map<number, number>();

    hasReachedFinalStateRef.current = false;

    const cleanupAll = () => {
      newWebsockets.forEach(ws => ws.close());
      pollingIntervalsRef.current.forEach(interval => clearInterval(interval));
      pollingIntervalsRef.current.clear();
    };

    const checkAllComplete = () => {
      if (confirmedSet.size === allPayloads.length) {
        hasReachedFinalStateRef.current = true;
        setStep('success');
        showSuccess('Bundle cancelled successfully!');
        cleanupAll();
      }
    };

    const updateProgress = () => {
      for (let i = 0; i < allPayloads.length; i++) {
        if (!confirmedSet.has(i)) {
          const nftName = getNftName(allPayloads[i].nftTokenId);
          setCurrentPayload(allPayloads[i]);
          setSigningProgress({
            currentIndex: i,
            totalCount: allPayloads.length,
            currentNftName: nftName,
            completedCount: confirmedSet.size,
          });
          return;
        }
      }
    };

    // Poll for a specific payload by index
    const pollPayloadByIndex = async (index: number, uuid: string) => {
      if (hasReachedFinalStateRef.current || confirmedSet.has(index)) {
        const interval = pollingIntervalsRef.current.get(index);
        if (interval) {
          clearInterval(interval);
          pollingIntervalsRef.current.delete(index);
        }
        return;
      }

      const attempts = (pollingAttemptsMap.get(index) || 0) + 1;
      pollingAttemptsMap.set(index, attempts);

      if (attempts > MAX_POLLING_ATTEMPTS) {
        const nftName = getNftName(allPayloads[index].nftTokenId);
        if (!hasReachedFinalStateRef.current) {
          hasReachedFinalStateRef.current = true;
          setError(`Cancellation request expired for ${nftName}. Please try again.`);
          setStep('error');
          cleanupAll();
        }
        return;
      }

      try {
        const response = await apiClient.get<any>(`/offers/payload/${uuid}`);
        if (!response.success || !response.data) return;

        const { signed, resolved, expired, dispatched } = response.data;
        const nftName = getNftName(allPayloads[index].nftTokenId);

        if (expired) {
          if (!confirmedSet.has(index) && !hasReachedFinalStateRef.current) {
            hasReachedFinalStateRef.current = true;
            setError(`Cancellation request expired for ${nftName}. Please try again.`);
            setStep('error');
            cleanupAll();
          }
          return;
        }

        if (signed === true && (dispatched === 'tesSUCCESS' || !dispatched)) {
          confirmedSet.add(index);
          setSignedIndices(new Set(confirmedSet));
          const interval = pollingIntervalsRef.current.get(index);
          if (interval) {
            clearInterval(interval);
            pollingIntervalsRef.current.delete(index);
          }
          updateProgress();
          checkAllComplete();
          return;
        }

        if (signed === true && dispatched && dispatched !== 'tesSUCCESS') {
          if (!hasReachedFinalStateRef.current) {
            hasReachedFinalStateRef.current = true;
            setError(`Cancellation failed for ${nftName}: ${dispatched}`);
            setStep('error');
            cleanupAll();
          }
          return;
        }

        if (signed === false && resolved === true) {
          if (!hasReachedFinalStateRef.current) {
            hasReachedFinalStateRef.current = true;
            setError(`Cancellation rejected for ${nftName}.`);
            setStep('error');
            cleanupAll();
          }
          return;
        }
      } catch (err) {
        console.error(`[CancelBundle] Polling error for payload ${index}:`, err);
      }
    };

    // Start polling for a specific payload
    const startPollingForPayload = (index: number, uuid: string) => {
      if (pollingIntervalsRef.current.has(index)) return;

      console.log(`[CancelBundle] Starting polling fallback for payload ${index}`);
      pollingAttemptsMap.set(index, 0);

      pollPayloadByIndex(index, uuid);
      const interval = setInterval(() => pollPayloadByIndex(index, uuid), POLLING_INTERVAL);
      pollingIntervalsRef.current.set(index, interval);
    };

    allPayloads.forEach((item, index) => {
      const nftName = getNftName(item.nftTokenId);
      // Xaman-only path: payload (with websocketUrl) is always present here.
      if (!item.payload) return;
      const xamanPayload = item.payload;
      const ws = new WebSocket(xamanPayload.websocketUrl);

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.signed === true && data.txid) {
            confirmedSet.add(index);
            setSignedIndices(new Set(confirmedSet));
            updateProgress();
            checkAllComplete();
          } else if (data.signed === false) {
            if (!hasReachedFinalStateRef.current) {
              hasReachedFinalStateRef.current = true;
              setError(`Cancellation rejected for ${nftName}.`);
              setStep('error');
              cleanupAll();
            }
          } else if (data.expired === true) {
            if (!confirmedSet.has(index) && !hasReachedFinalStateRef.current) {
              hasReachedFinalStateRef.current = true;
              setError(`Cancellation request expired for ${nftName}. Please try again.`);
              setStep('error');
              cleanupAll();
            }
          }
        } catch {
          // Ignore parsing errors
        }
      };

      ws.onerror = () => {
        if (!confirmedSet.has(index) && !hasReachedFinalStateRef.current) {
          console.error(`[CancelBundle] WebSocket error for payload ${index}, falling back to polling`);
          ws.close();
          startPollingForPayload(index, xamanPayload.uuid);
        }
      };

      ws.onclose = () => {
        if (!confirmedSet.has(index) && !hasReachedFinalStateRef.current) {
          console.log(`[CancelBundle] WebSocket closed for payload ${index}, falling back to polling`);
          startPollingForPayload(index, xamanPayload.uuid);
        }
      };

      newWebsockets.push(ws);
    });

    setWebsockets(newWebsockets);
  }, [getNftName, showSuccess]);

  // Client-signing (joey-wc) per-step resolution: mark cancelled + advance.
  // Cancel has no per-step backend confirm — the bundle is already removed server-side.
  const handleWcStepSigned = useCallback((index: number) => {
    if (hasReachedFinalStateRef.current || wcConfirmedRef.current.has(index)) return;

    const allPayloads = normalizedPayloads;
    wcConfirmedRef.current.add(index);
    setSignedIndices(new Set(wcConfirmedRef.current));

    if (wcConfirmedRef.current.size === allPayloads.length) {
      hasReachedFinalStateRef.current = true;
      setStep('success');
      showSuccess('Bundle cancelled successfully!');
      return;
    }

    const nextIndex = index + 1;
    setCurrentStepIndex(nextIndex);
    setSigningProgress({
      currentIndex: nextIndex,
      totalCount: allPayloads.length,
      currentNftName: getNftName(allPayloads[nextIndex].nftTokenId),
      completedCount: wcConfirmedRef.current.size,
    });
  }, [normalizedPayloads, getNftName, showSuccess]);

  const handleWcStepFailed = useCallback((message: string) => {
    if (hasReachedFinalStateRef.current) return;
    hasReachedFinalStateRef.current = true;
    setError(message);
    setStep('error');
  }, []);

  // Start cancellation flow
  const handleConfirmCancel = async () => {
    if (!user?.xrpl_address) {
      setError('Please sign in to cancel');
      return;
    }

    setError('');
    setStep('signing');

    try {
      const result = await cancelBundle(bundle.id, user.xrpl_address);

      if (result.cancelPayloads.length === 0) {
        // No offers to cancel (bundle was pending with no confirmed items)
        setStep('success');
        showSuccess('Bundle cancelled successfully!');
        return;
      }

      setPayloads(result.cancelPayloads);

      // Detect provider from the first payload's signing intent.
      const provider = result.cancelPayloads[0]?.signingIntent?.provider ?? 'xaman';
      const clientSigning = getWalletProvider(provider as any).capabilities.clientSigning;
      setIsClientSigning(clientSigning);

      const firstNftName = getNftName(result.cancelPayloads[0].nftTokenId);

      if (clientSigning) {
        // Sequential WC signing: normalize all, drive via SigningStepMonitor (no WS/QR).
        const normalized = result.cancelPayloads.map((p) => ({
          nftTokenId: p.nftTokenId,
          payload: toSigningPayload(p.signingIntent ?? p.payload),
        }));
        setNormalizedPayloads(normalized);
        wcConfirmedRef.current = new Set();
        setCurrentStepIndex(0);
        setSigningProgress({
          currentIndex: 0,
          totalCount: result.cancelPayloads.length,
          currentNftName: firstNftName,
          completedCount: 0,
        });
        return;
      }

      // Xaman path: set up all websockets
      setupAllWebsockets(result.cancelPayloads);

      // Show first payload
      setCurrentPayload(result.cancelPayloads[0]);
      setSigningProgress({
        currentIndex: 0,
        totalCount: result.cancelPayloads.length,
        currentNftName: firstNftName,
        completedCount: 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel bundle');
      setStep('error');
    }
  };

  const handleClose = () => {
    websockets.forEach(ws => ws.close());
    pollingIntervalsRef.current.forEach(interval => clearInterval(interval));
    pollingIntervalsRef.current.clear();
    if (step === 'success') {
      onSuccess?.();
    }
    onClose();
  };

  const itemCount = bundle.itemCount || bundle.items?.length || 0;

  const getTitle = () => {
    switch (step) {
      case 'confirm':
        return 'Cancel Bundle';
      case 'signing':
        return 'Cancel NFT Offers';
      case 'success':
        return 'Bundle Cancelled';
      case 'error':
        return 'Error';
      default:
        return 'Cancel Bundle';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Content className="w-full max-w-[480px]">
        <div className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {getTitle()}
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Confirm Step */}
          {step === 'confirm' && (
            <div className="flex flex-col gap-4 w-full">
              <div className="flex items-start gap-3 p-4 bg-warning-50 rounded-lg border border-warning-200">
                <FeatherAlertTriangle className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <p className="text-body-bold text-warning-700">
                    Are you sure you want to cancel this bundle?
                  </p>
                  <p className="text-caption text-warning-600">
                    This will cancel all {itemCount} NFT sell offer{itemCount !== 1 ? 's' : ''} on the XRPL.
                    Your NFTs will no longer be listed for sale as a bundle.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2 p-4 bg-neutral-50 rounded-lg">
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">Bundle Price</span>
                  <span className="text-default-font">{formatPrice(bundle.priceXrp)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">NFTs in Bundle</span>
                  <span className="text-default-font">{itemCount}</span>
                </div>
              </div>

              <p className="text-caption text-subtext-color">
                You'll need to sign {itemCount} cancellation transaction{itemCount !== 1 ? 's' : ''} in Xaman (one per NFT).
              </p>

              <p className="text-caption text-subtext-color italic">
                Note: Signing the cancellations is optional. The bundle will be removed from the marketplace immediately,
                and your XRPL offers will expire automatically on their own.
              </p>
            </div>
          )}

          {/* Signing Step — Xaman (QR + WS/polling, unchanged) */}
          {step === 'signing' && signingProgress && currentPayload && currentPayload.payload && !isClientSigning && (
            <div className="flex flex-col items-center gap-6 w-full">
              {/* Progress */}
              <div className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body text-subtext-color">
                    Cancelling offers
                  </span>
                  <span className="text-body-bold text-default-font">
                    {signingProgress.completedCount + 1} of {signingProgress.totalCount}
                  </span>
                </div>
                <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-warning-500 transition-all duration-300"
                    style={{
                      width: `${((signingProgress.completedCount + 1) / signingProgress.totalCount) * 100}%`,
                    }}
                  />
                </div>
              </div>

              <p className="text-body text-subtext-color text-center">
                Cancel offer for: <span className="text-default-font font-medium">{signingProgress.currentNftName}</span>
              </p>

              {/* QR Code */}
              <img
                src={currentPayload.payload.qrUrl}
                alt="Scan with Xaman"
                className="w-48 h-48 rounded-lg border border-neutral-border"
              />

              <a
                href={currentPayload.payload.deepLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                <Button variant="brand-primary" className="w-full">
                  Open in Xaman
                </Button>
              </a>

              <p className="text-caption text-subtext-color flex items-center gap-2">
                <FeatherLoader className="w-4 h-4 animate-spin" />
                Waiting for signature...
              </p>

              <p className="text-caption text-subtext-color text-center italic">
                Signing these cancellations is optional. Your bundle has already been removed from the marketplace.
                You can close this modal - the offers will expire automatically.
              </p>
            </div>
          )}

          {/* Signing Step — client-signing (joey-wc): sequential approve prompt, no QR */}
          {step === 'signing' && signingProgress && isClientSigning && normalizedPayloads[currentStepIndex] && (
            <div className="flex flex-col items-center gap-6 w-full">
              {/* Progress */}
              <div className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body text-subtext-color">
                    Cancelling offers
                  </span>
                  <span className="text-body-bold text-default-font">
                    {signingProgress.completedCount + 1} of {signingProgress.totalCount}
                  </span>
                </div>
                <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-warning-500 transition-all duration-300"
                    style={{
                      width: `${((signingProgress.completedCount + 1) / signingProgress.totalCount) * 100}%`,
                    }}
                  />
                </div>
              </div>

              <FeatherLoader className="w-8 h-8 text-brand-600 animate-spin" />
              <p className="text-body text-default-font text-center">
                Approve cancellation for "{signingProgress.currentNftName}" in your wallet ({signingProgress.completedCount + 1} of {signingProgress.totalCount})
              </p>

              <SigningStepMonitor
                key={normalizedPayloads[currentStepIndex].payload.id}
                payload={normalizedPayloads[currentStepIndex].payload}
                onSigned={() => handleWcStepSigned(currentStepIndex)}
                onRejected={() => handleWcStepFailed('Cancellation was rejected.')}
                onExpired={() => handleWcStepFailed('Cancellation request expired. Please try again.')}
                onError={(msg) => handleWcStepFailed(msg)}
              />

              <p className="text-caption text-subtext-color text-center italic">
                Signing these cancellations is optional. Your bundle has already been removed from the marketplace.
                You can close this modal - the offers will expire automatically.
              </p>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="flex flex-col items-center gap-4 py-8 w-full">
              <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center">
                <FeatherCheck className="w-8 h-8 text-success-600" />
              </div>
              <p className="text-body-bold text-default-font text-center">
                Bundle Cancelled
              </p>
              <p className="text-body text-subtext-color text-center">
                Your bundle has been cancelled and the NFTs are no longer listed for sale.
              </p>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="w-16 h-16 rounded-full bg-error-100 flex items-center justify-center">
                <FeatherX className="w-8 h-8 text-error-600" />
              </div>
              <p className="text-body text-error-600 text-center">
                {error || 'Something went wrong'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-3">
            {step === 'confirm' && (
              <>
                <Button variant="neutral-secondary" onClick={handleClose}>
                  Keep Bundle
                </Button>
                <Button variant="destructive-primary" onClick={handleConfirmCancel}>
                  Cancel Bundle
                </Button>
              </>
            )}

            {step === 'signing' && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Close
              </Button>
            )}

            {(step === 'success' || step === 'error') && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default CancelBundleModal;
